import { AlertTriangle, X } from "lucide-react";
import { type ReactNode, useEffect } from "react";

interface ConfirmDialogProps {
  /** Title — short noun phrase ("Delete organization?"). */
  title: string;
  /** Body — can be a string or rich React content (e.g. emphasized
   * cascade scope). Stays under 3 sentences in practice. */
  body: ReactNode;
  /** Confirm button label. Default "Confirm" — pass a verb that
   * matches the action ("Delete", "Suspend", etc.). */
  confirmLabel?: string;
  /** Visual emphasis on the confirm button. ``danger`` paints it
   * red (use for destructive actions). */
  variant?: "default" | "danger";
  /** Caller is mid-mutation — disables both buttons + swaps confirm
   * label to "Working…". */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Lightweight modal confirmation dialog. Backdrop + a centered
 * paper-warm card with primary + cancel actions. Esc cancels.
 *
 * Render inline only when the user has triggered an action that
 * needs confirmation — not as an always-present component. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel = "Confirm",
  variant = "default",
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  return (
    // biome-ignore lint/a11y/useSemanticElements: custom modal pattern; native <dialog> has different semantics (showModal/close lifecycle, top-layer rendering) that this component doesn't use.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/40 px-6 py-12"
      onClick={(e) => {
        // Click on backdrop closes; clicks inside the card don't bubble here.
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      onKeyDown={(e) => {
        // Esc closes (mirrors the window-level handler so backdrop-focused
        // keyboard users get an element-local affordance too).
        if (e.key === "Escape" && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-rule bg-paper-warm shadow-lg">
        <div className="flex items-start gap-3 px-5 py-4">
          {variant === "danger" && (
            <span className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-red-pencil/10">
              <AlertTriangle className="h-4 w-4 text-red-pencil" aria-hidden />
            </span>
          )}
          <div className="flex-1">
            <h2
              id="confirm-dialog-title"
              className="font-mono text-base font-bold tracking-tight text-graphite"
            >
              {title}
            </h2>
            <div className="mt-2 text-sm leading-relaxed text-graphite-soft">{body}</div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            aria-label="Cancel"
            className="text-graphite-soft hover:text-graphite disabled:opacity-50"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="flex justify-end gap-2 border-t border-rule bg-paper px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft transition hover:text-graphite disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={[
              "rounded px-3 py-1.5 font-mono text-sm font-semibold text-paper transition disabled:opacity-50",
              variant === "danger"
                ? "bg-red-pencil hover:bg-red-pencil/90"
                : "bg-graphite hover:bg-graphite-soft",
            ].join(" ")}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
